import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTemplateStore } from "@/store/useTemplateStore";
import { useEvaluationStore } from "@/store/useEvaluationStore";
import { useAutoSave } from "@/hooks/useAutoSave";
import { useToast } from "@/hooks/useToast";
import { computeEditorInit } from "@/components/editor/useEditorInit";
import { useBuildSave } from "@/components/editor/useBuildSave";
import { useReportGeneration } from "@/components/editor/useReportGeneration";
import { useTocNavigation } from "@/components/editor/useTocNavigation";
import { useScrollSync } from "@/components/editor/useScrollSync";
import { useFloatInsert } from "@/components/editor/useFloatInsert";
import { useEditorHandlers } from "@/components/editor/useEditorHandlers";
import { useEditorState } from "@/components/editor/useEditorState";
import { useSourceSync } from "@/components/editor/useSourceSync";
import type { Template } from "@/types/template";
import type { Evaluation } from "@/types/evaluation";

function useEditorStores(mode: "build" | "fill", id?: string, evalId?: string) {
  const template = useTemplateStore((s) => (mode === "build" && id ? s.getById(id) : undefined));
  const templateSave = useTemplateStore((s) => s.save);
  const templateRemove = useTemplateStore((s) => s.remove);
  const cloneFromSeed = useTemplateStore((s) => s.cloneFromSeed);
  const allTemplates = useTemplateStore((s) => s.templates);
  const evaluation = useEvaluationStore((s) =>
    mode === "fill" && evalId ? s.getById(evalId) : undefined,
  );
  const evalSave = useEvaluationStore((s) => s.save);
  const evalRemove = useEvaluationStore((s) => s.remove);
  return {
    allTemplates,
    cloneFromSeed,
    evalRemove,
    evalSave,
    evaluation,
    template,
    templateRemove,
    templateSave,
  };
}

function useEditorPersistence(
  mode: "build" | "fill",
  evaluation: Evaluation | undefined,
  state: ReturnType<typeof useEditorState>,
  template: Template | undefined,
  templateSave: (template: Template) => Promise<void>,
) {
  const { cancel: cancelAutoSave, status: autoSaveStatus } = useAutoSave(
    mode === "fill" ? (evaluation ?? null) : null,
    {
      fieldToggles: state.fieldToggles,
      fillVariables: state.variableValues,
      name: state.name,
      sectionToggles: state.sectionToggles,
      subTemplateValues: state.subTemplateValues,
      unifiedDocument: state.unifiedDocument as Record<string, unknown> | null,
      values: state.values,
    },
  );
  const { buildSaveStatus, debouncedSave } = useBuildSave(template, templateSave);
  const saveStatus = mode === "fill" ? autoSaveStatus : buildSaveStatus;
  return { cancelAutoSave, debouncedSave, saveStatus };
}

function useSourceAndReport(
  mode: "build" | "fill",
  state: ReturnType<typeof useEditorState>,
  debouncedSave: ReturnType<typeof useBuildSave>["debouncedSave"],
  initialSource: string,
) {
  const { cmViewRef, lineMapRef, syncDocToMd } = useSourceSync({
    codeMirrorContainerRef: state.codeMirrorContainerRef,
    debouncedSave,
    editorRef: state.editorRef,
    initialSource,
    meta: state.meta,
    mode,
    scrollInitiatorRef: state.scrollInitiatorRef,
    scrollInitiatorTimerRef: state.scrollInitiatorTimerRef,
    setMeta: state.setMeta,
    setParseErrors: state.setParseErrors,
  });
  const { interpolate, reportLineMapRef, reportSections } = useReportGeneration(
    state.unifiedDocument,
    state.values,
    state.subTemplateValues,
    state.variableValues,
    state.sectionToggles,
    state.fieldToggles,
  );
  return { cmViewRef, interpolate, lineMapRef, reportLineMapRef, reportSections, syncDocToMd };
}

function useEditorFeatures(
  mode: "build" | "fill",
  state: ReturnType<typeof useEditorState>,
  debouncedSave: ReturnType<typeof useBuildSave>["debouncedSave"],
  initialSource: string,
) {
  const { cmViewRef, interpolate, lineMapRef, reportLineMapRef, reportSections, syncDocToMd } =
    useSourceAndReport(mode, state, debouncedSave, initialSource);
  const toc = useTocNavigation(
    state.unifiedDocument,
    state.subTemplateValues,
    state.sectionToggles,
    state.editorRef,
    state.editorWrapperEl,
    state.scrollInitiatorRef,
    state.scrollInitiatorTimerRef,
  );
  useScrollSync(
    state.editorWrapperEl,
    state.editorRef,
    state.showSource,
    state.showReport,
    cmViewRef,
    lineMapRef,
    reportLineMapRef,
    state.reportPaneRef,
    state.scrollInitiatorRef,
    state.scrollInitiatorTimerRef,
    state.tiptapEditingRef,
    state.collapsedSections,
    toc.setActiveHeadingIndex,
  );
  const float = useFloatInsert(state.editorWrapperEl, state.editorRef);
  return { float, interpolate, reportSections, syncDocToMd, toc };
}

function buildHandlerParams(
  mode: "build" | "fill",
  stores: ReturnType<typeof useEditorStores>,
  state: ReturnType<typeof useEditorState>,
  features: ReturnType<typeof useEditorFeatures>,
  debouncedSave: ReturnType<typeof useBuildSave>["debouncedSave"],
  cancelAutoSave: () => void,
  toast: (msg: string, type?: "success" | "error" | "info") => void,
  navigate: (path: string) => void | Promise<void>,
) {
  return {
    allHeadings: features.toc.allHeadings,
    allTemplates: stores.allTemplates,
    cancelAutoSave,
    cloneFromSeed: stores.cloneFromSeed,
    collapsedSections: state.collapsedSections,
    debouncedSave,
    editorRef: state.editorRef,
    evalRemove: stores.evalRemove,
    evalSave: stores.evalSave,
    evaluation: stores.evaluation,
    fieldToggles: state.fieldToggles,
    interpolate: features.interpolate,
    meta: state.meta,
    mode,
    name: state.name,
    navigate,
    pendingInsertPosRef: features.float.pendingInsertPosRef,
    scrollToHeading: features.toc.scrollToHeading,
    sectionToggles: state.sectionToggles,
    setCollapsedSections: state.setCollapsedSections,
    setFieldToggles: state.setFieldToggles,
    setMeta: state.setMeta,
    setName: state.setName,
    setSectionToggles: state.setSectionToggles,
    setShowInsertPicker: state.setShowInsertPicker,
    setSubTemplateValues: state.setSubTemplateValues,
    setValues: state.setValues,
    subTemplateValues: state.subTemplateValues,
    template: stores.template,
    templateRemove: stores.templateRemove,
    toast,
    unifiedDocument: state.unifiedDocument,
    values: state.values,
    variableValues: state.variableValues,
  };
}

export function useEditorPage(mode: "build" | "fill") {
  const { evalId, id } = useParams<{ id?: string; evalId?: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const stores = useEditorStores(mode, id, evalId);
  const [initialData] = useState(() => computeEditorInit(mode, stores.template, stores.evaluation));
  const state = useEditorState(mode, stores.evaluation, initialData);
  const { cancelAutoSave, debouncedSave, saveStatus } = useEditorPersistence(
    mode,
    stores.evaluation,
    state,
    stores.template,
    stores.templateSave,
  );
  const features = useEditorFeatures(mode, state, debouncedSave, initialData.initialSource);
  const params = buildHandlerParams(
    mode,
    stores,
    state,
    features,
    debouncedSave,
    cancelAutoSave,
    toast,
    navigate,
  );
  const handlers = useEditorHandlers(params);
  return { features, handlers, navigate, saveStatus, state, stores };
}
